// Copyright (c) 2024 Goffy. This file belongs to Goffy. All Rights Reserved.

import { ScrollView, StyleSheet, Pressable, Platform } from 'react-native';
import { useEffect, useMemo, useState } from 'react';
import { Redirect, useLocalSearchParams, useRouter, useRootNavigationState } from 'expo-router';
import { formatHoleDistance } from '@/features/units/distance';
import { useDistanceUnit } from '@/stores/distanceUnitState';

import { Text, View } from '@/components/Themed';
import { useUser } from './user-context';
import { CourseAnalysisStatusPanel } from '@/features/course-trace/CourseAnalysisStatusPanel';
import { useEffectiveRoles } from '@/hooks/useEffectiveRoles';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import appColors from '@/theme/colors';

type Hole = {
  id: string;
  courseId: string;
  number: number;
  par: number;
  strokeIndex: number;
  lengthMeters: number;
};

type CourseDetail = {
  id: string;
  name: string;
  address: string;
  lat: number;
  lon: number;
  source?: string;
  createdAt?: string;
  updatedAt?: string;
  holes?: Hole[];
};

export default function CourseDetailsScreen() {
  const distanceUnit = useDistanceUnit();
  const { courseId } = useLocalSearchParams<{ courseId?: string }>();
  const router = useRouter();
  const navState = useRootNavigationState();
  const isNavigationReady = !!navState?.key;
  const { user } = useUser();
  const { isAdmin } = useEffectiveRoles(user);
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const [course, setCourse] = useState<CourseDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCourse = async () => {
      if (!courseId) {
        setError('Inget bana-ID angivet.');
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`${apiBaseUrl}/api/geodata/courses/${courseId}`, {
          headers: user?.token ? { Authorization: `Bearer ${user.token}` } : undefined,
        });
        if (response.status === 404) {
          setError('Banan hittades inte.');
          return;
        }
        if (!response.ok) {
          const message = await response.text();
          throw new Error(message || 'Kunde inte hämta banan.');
        }
        const payload = (await response.json()) as CourseDetail;
        setCourse({
          ...payload,
          holes: (payload.holes ?? []).slice().sort((a, b) => a.number - b.number),
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Kunde inte hämta banan.');
      } finally {
        setLoading(false);
      }
    };

    if (isNavigationReady && user) {
      fetchCourse().catch(() => {});
    }
  }, [apiBaseUrl, courseId, isNavigationReady, user]);

  if (!isNavigationReady) {
    return null;
  }

  if (!user) {
    return <Redirect href="/" />;
  }

  return (
    <ScrollView style={styles.scroll} contentContainerStyle={styles.container}>
      <View style={styles.headerRow}>
        <Pressable style={styles.backButton} onPress={() => router.back()}>
          <Text style={styles.backText}>←</Text>
        </Pressable>
        <Text style={styles.title}>Bana</Text>
      </View>

      {loading ? <Text style={styles.subtitle}>Hämtar bana...</Text> : null}
      {error ? <Text style={styles.error}>{error}</Text> : null}

      {course ? (
        <View style={styles.card} lightColor="#07200D" darkColor="#07200D">
          <Text style={styles.courseName}>{course.name}</Text>
          <Text style={styles.courseAddress}>{course.address}</Text>
          <View style={styles.metaRow}>
            <Text style={styles.meta}>Lat: {course.lat.toFixed(5)}</Text>
            <Text style={styles.meta}>Lon: {course.lon.toFixed(5)}</Text>
          </View>
          {course.source ? <Text style={styles.meta}>Källa: {course.source}</Text> : null}
          {course.holes && course.holes.length > 0 ? (
            <View style={styles.holesBlock}>
              <Text style={styles.sectionTitle}>Hål</Text>
              {course.holes.map((hole) => (
                <View key={hole.id} style={styles.holeRow}>
                  <Text style={styles.holeNumber}>Hål {hole.number}</Text>
                  <View style={styles.holeTags}>
                    <Text style={styles.tag}>Par {hole.par}</Text>
                    <Text style={styles.tag}>Hcp {hole.strokeIndex}</Text>
                    <Text style={styles.tag}>{formatHoleDistance(hole.lengthMeters, distanceUnit)}</Text>
                  </View>
                </View>
              ))}
            </View>
          ) : (
            <Text style={styles.subtitle}>Inga håldetaljer för den här banan.</Text>
          )}
        </View>
      ) : null}

      {/* Admin only. Reports whether imagery analysis has run for this course and what it
          found — the state that otherwise has to be pieced together from a Pulumi config
          value, a database row and a startup log line. */}
      {isAdmin && course ? (
        <CourseAnalysisStatusPanel
          apiBaseUrl={apiBaseUrl}
          token={user?.token}
          courseId={course.id}
        />
      ) : null}
    </ScrollView>
  );
}

const cardShadow = Platform.select({
  web: { boxShadow: '0px 2px 6px rgba(0,0,0,0.2)' },
  default: {
    shadowColor: '#C8C8B0',
    shadowOpacity: 0.2,
    shadowRadius: 5,
    shadowOffset: { width: 0, height: 2 },
  },
});

const styles = StyleSheet.create({
  scroll: {
    backgroundColor: '#011806',
  },
  container: {
    flexGrow: 1,
    padding: 16,
    gap: 12,
    backgroundColor: '#011806',
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  backButton: {
    paddingVertical: 8,
    paddingHorizontal: 10,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: '#A1D493',
    backgroundColor: '#07200D',
  },
  backText: {
    color: appColors.text.primary,
    fontWeight: '700',
    fontSize: 16,
  },
  title: {
    fontSize: 22,
    fontWeight: '700',
    color: appColors.text.primary,
  },
  subtitle: {
    color: appColors.text.primary,
  },
  error: {
    color: '#ff7b7b',
  },
  card: {
    padding: 14,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: '#1A3A20',
    backgroundColor: '#07200D',
    gap: 10,
    ...cardShadow,
  },
  courseName: {
    fontSize: 18,
    fontWeight: '800',
    color: appColors.text.primary,
  },
  courseAddress: {
    color: appColors.text.primary,
  },
  metaRow: {
    flexDirection: 'row',
    gap: 12,
    flexWrap: 'wrap',
  },
  meta: {
    color: '#A1D493',
    fontSize: 13,
  },
  holesBlock: {
    gap: 8,
    marginTop: 4,
  },
  sectionTitle: {
    fontWeight: '700',
    color: appColors.text.primary,
  },
  holeRow: {
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#1A3A20',
    gap: 4,
  },
  holeNumber: {
    color: appColors.text.primary,
    fontWeight: '700',
  },
  holeTags: {
    flexDirection: 'row',
    gap: 8,
    flexWrap: 'wrap',
  },
  tag: {
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 8,
    backgroundColor: '#1A3A20',
    borderWidth: 1,
    borderColor: '#1A3A20',
    color: appColors.text.primary,
    fontSize: 12,
    fontWeight: '700',
  },
});
